import type { ReactNode } from "react";
import { cn } from "@ascleo/ui/lib/utils";

export interface PulseRingIconProps {
  /** Centered icon (lucide element); sized to 48px. */
  icon: ReactNode;
  /** Chip styling — background, text color, and (optionally) size/radius overrides. */
  className?: string;
}

/**
 * An icon in a rounded chip emitting three expanding concentric rings — the
 * alarm / ringing motif used by the emergency and phone-contact screens. Rings
 * use `currentColor` and are gated behind `prefers-reduced-motion`.
 *
 * @example
 * ```tsx
 * <PulseRingIcon icon={<Siren />} className="bg-white/20 text-white" />
 * ```
 */
export function PulseRingIcon({ icon, className }: PulseRingIconProps) {
  return (
    <span
      className={cn(
        "kiosk-pulse-ring relative grid size-28 place-items-center rounded-[32px] [&>svg]:size-12",
        className,
      )}
    >
      <span aria-hidden className="kiosk-pulse-ring__ring" />
      <span
        aria-hidden
        className="kiosk-pulse-ring__ring"
        style={{ animationDelay: "0.8s" }}
      />
      <span
        aria-hidden
        className="kiosk-pulse-ring__ring"
        style={{ animationDelay: "1.6s" }}
      />
      {icon}
      <style>{`
        .kiosk-pulse-ring__ring {
          position: absolute; inset: 0; border-radius: inherit;
          border: 2px solid currentColor; opacity: 0; pointer-events: none;
        }
        @media (prefers-reduced-motion: no-preference) {
          .kiosk-pulse-ring__ring { animation: kioskPulseRing 2.4s ease-out infinite; }
        }
        @keyframes kioskPulseRing {
          0%   { transform: scale(1);   opacity: 0.55; }
          100% { transform: scale(1.6); opacity: 0; }
        }
      `}</style>
    </span>
  );
}
